/**
 * Exchange - Virtual coin definitions (30-coin pool, "living market").
 * 3 majors (permanent, always listed, low volatility) + 27 meme candidates
 * that rotate through the listing lifecycle (list -> delist -> revive).
 * All names are original parody inventions and all prices are fully
 * simulated - no real coins or market data are referenced.
 */

export type CoinTier = 'major' | 'meme';

export interface CoinConfig {
  id: string;
  /** Ticker symbol shown in UI (placeholder icon uses first 2 chars) */
  symbol: string;
  /** i18n key for display name */
  nameKey: string;
  tier: CoinTier;
  /** Majors are permanently listed and never rotate out of the exchange */
  permanent?: boolean;
  /** Initial price (also the mean-reversion anchor) */
  startPrice: number;
  /** Hard floor - price never goes below this */
  minPrice: number;
  /** Per-tick noise scale as a fraction of price (e.g. 0.03 = +-3%) */
  volatility: number;
  /** Momentum persistence 0-1. Higher = trends last longer */
  momentumDecay: number;
  /** Mean reversion strength (applied in log space toward startPrice) */
  meanReversionStrength: number;
  /** Placeholder icon color (CSS hex) */
  color: string;
}

export const COINS: CoinConfig[] = [
  // === Majors (permanent, low volatility) - the "blue chips" ===
  {
    id: 'stonecoin', symbol: 'STN', nameKey: 'coin_stonecoin', tier: 'major', permanent: true,
    startPrice: 68000, minPrice: 1000, volatility: 0.004, momentumDecay: 0.75, meanReversionStrength: 0.02, color: '#94A3B8',
  },
  {
    id: 'nebula', symbol: 'NBL', nameKey: 'coin_nebula', tier: 'major', permanent: true,
    startPrice: 3200, minPrice: 50, volatility: 0.005, momentumDecay: 0.78, meanReversionStrength: 0.018, color: '#9B5DE5',
  },
  {
    id: 'lattice', symbol: 'LAT', nameKey: 'coin_lattice', tier: 'major', permanent: true,
    startPrice: 540, minPrice: 20, volatility: 0.006, momentumDecay: 0.76, meanReversionStrength: 0.016, color: '#38BDF8',
  },

  // === Meme candidates (rotate through listing lifecycle) ===
  // The original 3 keep their generated icon images.
  {
    id: 'chickencoin', symbol: 'CHK', nameKey: 'coin_chickencoin', tier: 'meme',
    startPrice: 0.12, minPrice: 0.0001, volatility: 0.012, momentumDecay: 0.8, meanReversionStrength: 0.008, color: '#F4A261',
  },
  {
    id: 'moonfrog', symbol: 'MFG', nameKey: 'coin_moonfrog', tier: 'meme',
    startPrice: 0.0042, minPrice: 0.000001, volatility: 0.016, momentumDecay: 0.82, meanReversionStrength: 0.006, color: '#4CAF50',
  },
  {
    id: 'pumpking', symbol: 'PKG', nameKey: 'coin_pumpking', tier: 'meme',
    startPrice: 1.5, minPrice: 0.001, volatility: 0.02, momentumDecay: 0.85, meanReversionStrength: 0.004, color: '#FF5C8A',
  },

  // Fallback-icon meme pool (colored initial circle until batch art lands).
  { id: 'moonshot', symbol: 'MSH', nameKey: 'coin_moonshot', tier: 'meme', startPrice: 0.34, minPrice: 0.0001, volatility: 0.0216, momentumDecay: 0.84, meanReversionStrength: 0.005, color: '#8FA8FF' },
  { id: 'pizzaswap', symbol: 'PZA', nameKey: 'coin_pizzaswap', tier: 'meme', startPrice: 2.7, minPrice: 0.002, volatility: 0.0204, momentumDecay: 0.83, meanReversionStrength: 0.005, color: '#F2994A' },
  { id: 'catchain', symbol: 'CAT', nameKey: 'coin_catchain', tier: 'meme', startPrice: 0.089, minPrice: 0.00005, volatility: 0.024, momentumDecay: 0.85, meanReversionStrength: 0.004, color: '#E0A96D' },
  { id: 'rocksolid', symbol: 'RCK', nameKey: 'coin_rocksolid', tier: 'meme', startPrice: 12, minPrice: 0.01, volatility: 0.0132, momentumDecay: 0.79, meanReversionStrength: 0.008, color: '#9AA5B1' },
  { id: 'yolocoin', symbol: 'YOL', nameKey: 'coin_yolocoin', tier: 'meme', startPrice: 0.0007, minPrice: 0.0000001, volatility: 0.0312, momentumDecay: 0.87, meanReversionStrength: 0.003, color: '#FF6B6B' },
  { id: 'ramenfi', symbol: 'RMN', nameKey: 'coin_ramenfi', tier: 'meme', startPrice: 4.2, minPrice: 0.003, volatility: 0.018, momentumDecay: 0.82, meanReversionStrength: 0.006, color: '#F5D061' },
  { id: 'ghostnet', symbol: 'GHO', nameKey: 'coin_ghostnet', tier: 'meme', startPrice: 0.021, minPrice: 0.00001, volatility: 0.0264, momentumDecay: 0.86, meanReversionStrength: 0.004, color: '#C8BFE7' },
  { id: 'crabdao', symbol: 'CRB', nameKey: 'coin_crabdao', tier: 'meme', startPrice: 0.55, minPrice: 0.0005, volatility: 0.0192, momentumDecay: 0.83, meanReversionStrength: 0.005, color: '#FF8A5C' },
  { id: 'tacotoken', symbol: 'TAC', nameKey: 'coin_tacotoken', tier: 'meme', startPrice: 1.1, minPrice: 0.001, volatility: 0.0216, momentumDecay: 0.84, meanReversionStrength: 0.005, color: '#E9C46A' },
  { id: 'sockswap', symbol: 'SCK', nameKey: 'coin_sockswap', tier: 'meme', startPrice: 0.16, minPrice: 0.0001, volatility: 0.0228, momentumDecay: 0.85, meanReversionStrength: 0.004, color: '#6FD6C2' },
  { id: 'ufomoney', symbol: 'UFO', nameKey: 'coin_ufomoney', tier: 'meme', startPrice: 0.0033, minPrice: 0.0000005, volatility: 0.0288, momentumDecay: 0.87, meanReversionStrength: 0.003, color: '#7FE7F5' },
  { id: 'doomfloor', symbol: 'DMF', nameKey: 'coin_doomfloor', tier: 'meme', startPrice: 0.42, minPrice: 0.0002, volatility: 0.0276, momentumDecay: 0.86, meanReversionStrength: 0.004, color: '#B45309' },
  { id: 'lambodream', symbol: 'LMB', nameKey: 'coin_lambodream', tier: 'meme', startPrice: 0.0088, minPrice: 0.000005, volatility: 0.03, momentumDecay: 0.87, meanReversionStrength: 0.003, color: '#F97316' },
  { id: 'diamondpaw', symbol: 'DMD', nameKey: 'coin_diamondpaw', tier: 'meme', startPrice: 0.73, minPrice: 0.0006, volatility: 0.0204, momentumDecay: 0.83, meanReversionStrength: 0.005, color: '#60A5FA' },
  { id: 'rugbeater', symbol: 'RGB', nameKey: 'coin_rugbeater', tier: 'meme', startPrice: 0.19, minPrice: 0.0001, volatility: 0.0252, momentumDecay: 0.85, meanReversionStrength: 0.004, color: '#A855F7' },
  { id: 'sushirocket', symbol: 'SSR', nameKey: 'coin_sushirocket', tier: 'meme', startPrice: 3.4, minPrice: 0.002, volatility: 0.0192, momentumDecay: 0.83, meanReversionStrength: 0.005, color: '#EF4444' },
  { id: 'nyanfi', symbol: 'NYA', nameKey: 'coin_nyanfi', tier: 'meme', startPrice: 0.0015, minPrice: 0.0000002, volatility: 0.0324, momentumDecay: 0.88, meanReversionStrength: 0.003, color: '#F472B6' },
  { id: 'gigachad', symbol: 'GGC', nameKey: 'coin_gigachad', tier: 'meme', startPrice: 6.9, minPrice: 0.004, volatility: 0.0228, momentumDecay: 0.85, meanReversionStrength: 0.004, color: '#22D3EE' },
  { id: 'kimchibase', symbol: 'KMB', nameKey: 'coin_kimchibase', tier: 'meme', startPrice: 0.28, minPrice: 0.0002, volatility: 0.024, momentumDecay: 0.85, meanReversionStrength: 0.004, color: '#DC2626' },
  { id: 'popcat', symbol: 'POP', nameKey: 'coin_popcat', tier: 'meme', startPrice: 0.052, minPrice: 0.00003, volatility: 0.0264, momentumDecay: 0.86, meanReversionStrength: 0.004, color: '#FBBF24' },
  { id: 'baguettefi', symbol: 'BGT', nameKey: 'coin_baguettefi', tier: 'meme', startPrice: 1.9, minPrice: 0.001, volatility: 0.018, momentumDecay: 0.82, meanReversionStrength: 0.006, color: '#D97706' },
  { id: 'vaporwave', symbol: 'VPR', nameKey: 'coin_vaporwave', tier: 'meme', startPrice: 0.011, minPrice: 0.000005, volatility: 0.0288, momentumDecay: 0.87, meanReversionStrength: 0.003, color: '#C084FC' },
  { id: 'brrrcoin', symbol: 'BRR', nameKey: 'coin_brrrcoin', tier: 'meme', startPrice: 0.0025, minPrice: 0.0000003, volatility: 0.0312, momentumDecay: 0.88, meanReversionStrength: 0.003, color: '#34D399' },
  { id: 'panicsell', symbol: 'PNC', nameKey: 'coin_panicsell', tier: 'meme', startPrice: 0.37, minPrice: 0.0002, volatility: 0.0276, momentumDecay: 0.86, meanReversionStrength: 0.004, color: '#64748B' },
];

const COIN_BY_ID = new Map(COINS.map(c => [c.id, c]));

export function getCoinConfig(coinId: string): CoinConfig | undefined {
  return COIN_BY_ID.get(coinId);
}

/** Rebrand generations shift the hue of the placeholder color a little per revive. */
export function rebrandColor(baseColor: string, generation: number): string {
  if (generation <= 0) return baseColor;
  const n = parseInt(baseColor.slice(1), 16);
  const shift = (v: number) => Math.max(0, Math.min(255, Math.round(v + (generation * 26 - 13))));
  const r = shift((n >> 16) & 0xff), g = shift((n >> 8) & 0xff), b = shift(n & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Candle timeframes (ms per candle bucket) */
export const TIMEFRAMES = {
  m1: 60_000,
  m5: 300_000,
  h1: 3_600_000,
} as const;

export type Timeframe = keyof typeof TIMEFRAMES;

export const EXCHANGE_CONFIG = {
  /** ms between price ticks */
  tickInterval: 2000,
  /** max candles kept per timeframe (memory / save size cap) */
  maxCandles: 60,
  /** momentum hard clamp = volatility * this factor */
  momentumClampFactor: 4,

  // === Global sentiment bias (v2.0) ===
  // Old market earningsMultiplier (0.3~2.0) biases every coin's momentum:
  // bias = (multiplier - anchor) * k * volatility per tick.
  // Majors follow the macro mood; memes are dominated by their own action.
  sentimentAnchor: 1.15,
  sentimentKMajor: 0.5,
  sentimentKMeme: 0.2,
} as const;
